import { getExtensionValue } from "@medplum/core";
import { QuestionnaireItem, QuestionnaireResponseItem } from "@medplum/fhirtypes";

import { Title, TitleOrder } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";

import { IconCornerDownRight } from "@tabler/icons-react";

import { TextWrapper } from "@/components/TextWrapper";
import { Extensions } from "@/fhir/enums/extensions";
import { hasExtensionValue } from "@/utils/medplum";

import QuestionnaireForm from "../QuestionnaireForm";

const QuestionnaireGroupField = <T,>({
  form,
  formFieldName,
  group,
  questionnaireResponse,
  label,
  groupLinkId,
  groupTitleOrder,
}: {
  group: QuestionnaireItem | undefined;
  form: UseFormReturnType<T>;
  formFieldName: string;
  questionnaireResponse?: QuestionnaireResponseItem;
  label?: string;
  groupLinkId: string;
  groupTitleOrder?: TitleOrder;
}) => {
  const findNestedGroup = (
    items: QuestionnaireItem[] | undefined,
    linkId: string,
  ): QuestionnaireItem | undefined => {
    if (!items) return undefined;

    for (const item of items) {
      if (item.linkId === linkId) return item;
      if (item.item) {
        const nested = findNestedGroup(item.item, linkId);
        if (nested) return nested;
      }
    }
    return undefined;
  };

  const nestedGroup = findNestedGroup(group?.item, groupLinkId);

  // Check if this group should be rendered horizontally
  const isHorizontal = Boolean(
    nestedGroup?.extension?.some(
      (ext) => ext.valueCodeableConcept?.coding?.[0]?.code === "horizontal-group",
    ),
  );

  const showFormsetLabel = getExtensionValue(
    nestedGroup,
    Extensions.QuestionnaireItemFormsetShowTitle,
  ) as boolean | undefined;

  const showLabel = label && (!nestedGroup?.repeats || showFormsetLabel) && !isHorizontal;

  const titleOrder = groupTitleOrder || 2;

  const isNestedGroupDisplay = hasExtensionValue(
    nestedGroup,
    Extensions.QuestionnaireItemControl,
    "nested-group",
  );

  return (
    <>
      {showLabel &&
        (isNestedGroupDisplay ? (
          <TextWrapper
            size="md"
            c="gray.7"
            variant="bold"
            style={{ display: "flex", alignItems: "center" }}
          >
            <IconCornerDownRight size={16} color="#868E96" style={{ marginRight: 8 }} /> {label}
          </TextWrapper>
        ) : (
          <Title order={titleOrder}>{label}</Title>
        ))}
      <QuestionnaireForm
        form={form}
        label={label}
        group={nestedGroup}
        parentPath={formFieldName}
        isHorizontal={isHorizontal}
        questionnaireResponse={questionnaireResponse}
      />
    </>
  );
};

export default QuestionnaireGroupField;
